import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import CleanSection from '../components/CleanSection';
import InstructorSection from '../components/InstructorSection';
import OurMovements from '../components/OurMovements';
import { Button } from '../junto-frontend/components/ui';
import Footer from '../components/Footer';
import HeroBackground from '../components/HeroBackground';
import { theme } from '../config/theme';
import { getImage, getStudioGallery } from '../config/imageRegistry';

const SectionSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.accent.bright};
  margin: 0;
`;

const HeroSection = styled(motion.section)`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  overflow: hidden;
  padding-top: var(--nav-height, 128px);
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.tablet}) {
    align-items: center;
    justify-content: center;
  }
`;

const HeroContent = styled(motion.div)`
  text-align: left;
  color: ${theme.colors.neutral.lightest};
  z-index: 2;
  max-width: 600px;
  padding-left: calc(${theme.spacing.sectionPadding.horizontal} + 40px);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
    padding: 0 ${theme.spacing.lg};
    max-width: 800px;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: 300;
  color: ${theme.colors.neutral.lightest};
  margin-bottom: ${theme.spacing.lg};
  letter-spacing: 0.02em;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.primary.mediumHeading.fontSize};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.primary.smallHeading.fontSize};
  }
`;

const AccentText = styled.span`
  color: ${theme.colors.accent.main};
`;


const HeroButton = styled(motion.div)`
  a {
    text-decoration: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    justify-content: center;
  }
  
  button {
    border-color: ${theme.colors.neutral.lightest};
    color: ${theme.colors.neutral.lightest};
    background: transparent;
    
    &:hover {
      background: ${theme.colors.neutral.lightest} !important;
      color: ${theme.colors.primary.text} !important;
    }
  }
`;


const InstagramSection = styled.section`
  padding: ${theme.spacing.sectionPadding.vertical} ${theme.spacing.sectionPadding.horizontal};
  background-color: ${theme.colors.neutral.lightest};
`;

const InstagramContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const InstagramTitle = styled.h2`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.md};
`;

const InstagramLink = styled.a`
  color: ${theme.colors.accent.main};
  text-decoration: underline;
  text-decoration-color: ${theme.colors.accent.main};
  text-underline-offset: 6px;
  text-decoration-thickness: 2px;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.accent.deep};
    text-decoration-color: ${theme.colors.accent.deep};
  }
`;

const InstagramSubtitle = styled.p`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  opacity: 0.8;
`;

const InstagramEmbed = styled.div`
  width: min(100vw - 2rem, 700px);
  height: min(110vw - 2rem, 700px);
  max-width: 700px;
  max-height: 700px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px ${theme.shadows.medium};
  display: flex;
  justify-content: center;
  align-items: center;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
    overflow: hidden;
  }
`;

const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    // Only handle anchor scrolling when on the homepage and hash changes
    if (location.pathname === '/' && location.hash) {
      const targetId = location.hash.substring(1); // Remove the #
      
      // If someone tries to access #location, redirect to the location page
      if (targetId === 'location') {
        window.location.href = '/location';
        return;
      }
      
      // For all other anchors, scroll to the element
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        // Use requestAnimationFrame for better timing coordination
        requestAnimationFrame(() => {
          setTimeout(() => {
            targetElement.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }, 150); // Slightly longer delay to ensure other scroll operations complete
        });
      }
    }
  }, [location.hash, location.pathname]);

  const heroVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: theme.animations.slow,
        staggerChildren: theme.animations.staggerMedium,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: theme.animations.moveMedium },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: theme.animations.medium,
        ease: 'easeOut',
      },
    },
  };

  return (
    <>
      <HeroSection
        initial="hidden"
        animate="visible"
        variants={heroVariants}
      >
        <HeroBackground src={getImage('home_page_hero_background')} />
        <HeroContent>
          <HeroTitle variants={itemVariants}>
            <AccentText>Rise</AccentText> and Shine at The Sol Room
          </HeroTitle>
          <HeroButton variants={itemVariants}>
            <Link to="/book">
              <Button>Book a Class</Button>
            </Link>
          </HeroButton>
        </HeroContent>
      </HeroSection>

      <div id="studio">
        <CleanSection
          headline="Our <em>Studio</em>"
          description="Natural light floods our beautifully designed space. Every detail curated for peaceful practice. State-of-the-art equipment in an atmosphere that feels like a retreat."
          images={getStudioGallery()}
          buttonText="Virtual Tour"
          buttonLink="/virtual-tour"
          galleryLink="/virtual-tour"
        />
      </div>

      <SectionSeparator />

      <div id="team">
        <InstructorSection />
      </div>

      <SectionSeparator />
      
      <div id="movements">
        <OurMovements />
      </div>

      <SectionSeparator />

      <div id="membership">
        <CleanSection
          headline="Join the <em>Family</em>"
          description="Can't get enough of our classes? Want exclusive merch and rewards that makes your neighbors jealous? First dibs on our events? Discounts on purchases?<br><br>We want you on-board, so we made it impossible to resist."
          image={getImage('home_page_join_the_family')}
          buttonText="Become a Member"
          buttonLink="/purchase#memberships"
        />
      </div>

      <SectionSeparator />
      
      {/* Instagram Section */}
      <InstagramSection>
        <InstagramContainer>
          <InstagramTitle>Follow <InstagramLink href="https://www.instagram.com/the.sol.room/" target="_blank" rel="noopener noreferrer">@the.sol.room</InstagramLink></InstagramTitle>
          <InstagramSubtitle>Checkout out what's happening at the studio</InstagramSubtitle>
          <InstagramEmbed>
            <iframe
              src="https://www.instagram.com/the.sol.room/embed"
              title="The Sol Room Instagram Feed"
              style={{ border: 'none', overflow: 'hidden' }}
            ></iframe>
          </InstagramEmbed>
        </InstagramContainer>
      </InstagramSection>
      
      <Footer />
    </>
  );
};

export default HomePage;